import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Plus, X, Heart, Search, ArrowLeft, Save,
  Check, Loader2, CheckCircle2,
  ChevronUp, ChevronDown, GripVertical
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import GuestAuthGate from "../components/GuestAuthGate";
import ValueIcon from "../components/ValueIcon";
import { EvoluraLogo } from "../components/EvoluraLogo";
import SEO from "../components/SEO";
import { fbViewContent } from "../utils/fbpixel";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { trackValuePlaced, trackValueRemoved, trackPyramidCompleted } from "../utils/analytics";

const API = "/api";
const GUEST_STORAGE_KEY = "evoluras_guest_values";

/* ─── Coaching labels for empty pyramid slots ─── */
const VALUE_SLOT_HINTS = [
  "Your most important value",
  "Guides your daily choices",
  "Guides your daily choices",
  "A value you want to grow into",
  "A value you want to grow into",
  "A value you want to grow into",
];
const ANTI_SLOT_HINTS = [
  "The pattern you most want to avoid",
  "A behavior that holds you back",
  "A behavior that holds you back",
  "A tendency to stay aware of",
  "A tendency to stay aware of",
  "A tendency to stay aware of",
];

/* ─── Onboarding step definitions ─── */
const ONBOARDING_STEPS = [
  {
    key: "start",
    message: "Browse the panel on the right and click + to add your first core value.",
    target: "values",
  },
  {
    key: "anti",
    message: "Great start! Now switch to Anti-Values and pick the patterns you want to avoid.",
    target: "anti",
  },
  {
    key: "complete",
    message: "Your identity diamond is taking shape. Keep going until it feels right!",
    target: null,
  },
];

/* ─────────── Browser Card (draggable from palette) ─────────── */
const BrowserCard = ({ value, type, onQuickAdd, isPlaced }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `browser-${type}-${value.id}`,
    data: { type: `browser-${type}`, value },
    disabled: isPlaced,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.3 : isPlaced ? 0.4 : 1,
  };

  const isValues = type === "values";

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`p-2.5 rounded-xl border transition-all select-none ${
        isPlaced
          ? "border-slate-100 bg-slate-50/60 cursor-default"
          : isValues
            ? "border-emerald-200/60 bg-white hover:border-emerald-400 hover:shadow-sm cursor-grab active:cursor-grabbing"
            : "border-red-200/60 bg-white hover:border-red-400 hover:shadow-sm cursor-grab active:cursor-grabbing"
      }`}
      data-testid={`browser-card-${value.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <ValueIcon
            id={value.id}
            name={value.name}
            anti={!isValues}
            size={28}
            className="mt-0.5"
            testId={`browser-icon-${value.id}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-sm text-slate-800 leading-tight">{value.name}</span>
              {isPlaced && <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">{value.definition}</p>
          </div>
        </div>
        {!isPlaced && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onQuickAdd(value); }}
            className={`p-1 rounded-lg transition-colors flex-shrink-0 mt-0.5 ${
              isValues ? "hover:bg-emerald-100 text-emerald-600" : "hover:bg-red-100 text-red-600"
            }`}
            data-testid={`quick-add-${value.id}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────── Placed Card (draggable within pyramid) ─────────── */
const PlacedCard = ({ value, slotIndex, type, onRemove, rankLabel }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `placed-${type}-${slotIndex}`,
    data: { type: `pyramid-${type}`, slotIndex, value },
  });

  const isValues = type === "values";
  const style = { transform: CSS.Transform.toString(transform) };

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`rounded-2xl border-2 border-dashed min-h-[68px] ${
          isValues ? "border-emerald-300 bg-emerald-50/40" : "border-red-300 bg-red-50/40"
        }`}
        data-testid={`placed-card-${type}-${slotIndex}`}
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-2xl border shadow-sm transition-all group ${
        isValues
          ? "bg-white border-emerald-200 hover:shadow-md hover:border-emerald-300"
          : "bg-white border-red-200 hover:shadow-md hover:border-red-300"
      }`}
      data-testid={`placed-card-${type}-${slotIndex}`}
    >
      {/* Drag handle strip */}
      <div
        {...attributes}
        {...listeners}
        className="flex items-center justify-center py-1 cursor-grab active:cursor-grabbing rounded-t-2xl hover:bg-slate-50/80 transition-colors"
        data-testid={`drag-handle-${type}-${slotIndex}`}
      >
        <GripVertical className="w-4 h-4 text-slate-300 group-hover:text-slate-400" />
      </div>
      <div className="px-2.5 pb-2.5">
        <div className="flex items-center gap-2">
          <div className="relative shrink-0">
            <ValueIcon
              id={value.id}
              name={value.name}
              anti={!isValues}
              size={32}
              testId={`placed-icon-${type}-${slotIndex}`}
            />
            <span className={`absolute -bottom-1 -right-1 text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${
              isValues ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
            }`}>
              {rankLabel.replace("#", "")}
            </span>
          </div>
          <span className="font-semibold text-xs text-slate-800 truncate leading-tight">{value.name}</span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed pl-[40px]">
          {value.definition || value.description}
        </p>
      </div>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(slotIndex); }}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-slate-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:border-red-300 z-10"
        data-testid={`remove-${type}-${slotIndex}`}
      >
        <X className="w-2.5 h-2.5 text-slate-400 hover:text-red-500" />
      </button>
    </div>
  );
};

/* ─────────── Pyramid Slot (droppable zone) ─────────── */
const PyramidSlot = ({ slotId, slotIndex, value, type, onRemove, activeDrag, rankLabel, hint }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: slotId,
    data: { type: `slot-${type}`, slotIndex },
  });

  const isValidTarget = activeDrag && (
    activeDrag.type === `browser-${type}` || activeDrag.type === `pyramid-${type}`
  );
  const highlight = isOver && isValidTarget;
  const isValues = type === "values";

  return (
    <div
      ref={setNodeRef}
      className={`rounded-2xl transition-all ${
        value
          ? highlight
            ? isValues
              ? "ring-2 ring-emerald-400 ring-offset-2 scale-[1.02]"
              : "ring-2 ring-red-400 ring-offset-2 scale-[1.02]"
            : ""
          : `border-2 border-dashed min-h-[68px] flex flex-col items-center justify-center px-2 py-3 ${
              highlight
                ? isValues
                  ? "border-emerald-400 bg-emerald-100/60 scale-[1.03]"
                  : "border-red-400 bg-red-100/60 scale-[1.03]"
                : isValues
                  ? "border-emerald-200/70 bg-emerald-50/20"
                  : "border-red-200/70 bg-red-50/20"
            }`
      }`}
      data-testid={`pyramid-slot-${type}-${slotIndex}`}
    >
      {value ? (
        <PlacedCard
          value={value}
          slotIndex={slotIndex}
          type={type}
          onRemove={onRemove}
          rankLabel={rankLabel}
        />
      ) : (
        <>
          <span className={`text-xs font-bold ${
            isValues ? "text-emerald-300" : "text-red-300"
          }`}>
            {rankLabel}
          </span>
          {hint && (
            <span className={`text-[9px] mt-0.5 text-center leading-tight ${
              isValues ? "text-emerald-300/80" : "text-red-300/80"
            }`}>
              {hint}
            </span>
          )}
        </>
      )}
    </div>
  );
};

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const CoreValuesTool = ({ guest = false }) => {
  const auth = useAuth();
  const user = guest ? null : auth?.user;
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Pyramid state: 6 slots each (null = empty)
  const [pyramidValues, setPyramidValues] = useState(Array(6).fill(null));
  const [pyramidAntiValues, setPyramidAntiValues] = useState(Array(6).fill(null));

  // Predefined data
  const [predefinedValues, setPredefinedValues] = useState([]);
  const [predefinedAntiValues, setPredefinedAntiValues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [antiCategories, setAntiCategories] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [browserTab, setBrowserTab] = useState("values");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0); // 0=start, 1=anti, 2=complete, 3=done

  const saveTimeoutRef = useRef(null);
  const completedRef = useRef(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  /* ─── Data Loading ─── */
  useEffect(() => {
    async function loadAll() {
      try {
        if (guest) {
          // Guest: only fetch predefined (public endpoint), load pyramid from localStorage.
          // Pass the i18n language so guests see localised content before signup.
          const langParam = encodeURIComponent(i18n?.language || "en");
          const predRes = await fetch(`${API}/tools/values/predefined?lang=${langParam}`);
          if (predRes.ok) {
            const pd = await predRes.json();
            setPredefinedValues(pd.values || []);
            setPredefinedAntiValues(pd.anti_values || []);
            setCategories(pd.categories || []);
            setAntiCategories(pd.anti_value_categories || []);
          }

          // Load saved guest state from localStorage
          const saved = localStorage.getItem(GUEST_STORAGE_KEY);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              const pv = Array(6).fill(null);
              (parsed.values || []).forEach((v, i) => { if (i < 6) pv[i] = v; });
              const pa = Array(6).fill(null);
              (parsed.anti_values || []).forEach((v, i) => { if (i < 6) pa[i] = v; });
              setPyramidValues(pv);
              setPyramidAntiValues(pa);
            } catch { /* ignore parse errors */ }
          }
        } else {
          // Authenticated: fetch user's saved values + predefined list
          const [valRes, predRes] = await Promise.all([
            fetch(`${API}/tools/values`, { credentials: "include" }),
            fetch(`${API}/tools/values/predefined`, { credentials: "include" }),
          ]);

          if (valRes.ok) {
            const data = await valRes.json();
            const saved = (data.values || []).sort((a, b) => a.priority - b.priority).slice(0, 6);
            const savedAnti = (data.anti_values || []).sort((a, b) => a.priority - b.priority).slice(0, 6);
            const pv = Array(6).fill(null);
            saved.forEach((v, i) => { pv[i] = v; });
            const pa = Array(6).fill(null);
            savedAnti.forEach((v, i) => { pa[i] = v; });
            setPyramidValues(pv);
            setPyramidAntiValues(pa);
          }

          if (predRes.ok) {
            const pd = await predRes.json();
            setPredefinedValues(pd.values || []);
            setPredefinedAntiValues(pd.anti_values || []);
            setCategories(pd.categories || []);
            setAntiCategories(pd.anti_value_categories || []);
          }
        }
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
        setInitialLoadDone(true);
      }
    }
    loadAll();
  }, [guest]);

  // FB Pixel: track tool view for guests
  useEffect(() => {
    if (guest) fbViewContent("Core Values", "self_assessment_tool");
  }, [guest]);

  /* ─── Save guest state to localStorage ─── */
  const saveGuestState = useCallback((vals, antiVals) => {
    const data = {
      values: vals.filter(Boolean),
      anti_values: antiVals.filter(Boolean),
    };
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
  }, []);

  /* ─── Auto-save ─── */
  const autoSave = useCallback(async (vals, antiVals) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      if (guest) {
        // Guest: save to localStorage
        saveGuestState(vals, antiVals);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(null), 1500);
        return;
      }
      // Authenticated: save to API
      setSaveStatus("saving");
      try {
        const res = await fetch(`${API}/tools/values/pyramid`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            values: vals.filter(Boolean).map(v => ({
              value_id: v.value_id,
              id: v.id || "",
              name: v.name,
              description: v.description || v.definition || "",
              definition: v.definition || "",
              category: v.category || "",
            })),
            anti_values: antiVals.filter(Boolean).map(v => ({
              value_id: v.value_id,
              id: v.id || "",
              name: v.name,
              description: v.description || v.definition || "",
              definition: v.definition || "",
              category: v.category || "",
            })),
          }),
        });
        if (res.ok) {
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus(null), 2000);
        } else throw new Error();
      } catch {
        toast.error("Failed to save");
        setSaveStatus(null);
      }
    }, 800);
  }, [guest, saveGuestState]);

  /* ─── Completion tracking ─── */
  const valCount = pyramidValues.filter(Boolean).length;
  const antiCount = pyramidAntiValues.filter(Boolean).length;
  useEffect(() => {
    if (valCount === 6 && antiCount === 6 && initialLoadDone && !completedRef.current) {
      completedRef.current = true;
      trackPyramidCompleted({ valuesCount: 6, antiValuesCount: 6 });
    }
  }, [valCount, antiCount, initialLoadDone]);

  /* ─── Onboarding step auto-progression ─── */
  useEffect(() => {
    if (onboardingStep >= 3) return; // done
    if (!initialLoadDone) return;
    // If user already has data on load, skip onboarding
    if (valCount > 0 && antiCount > 0 && onboardingStep === 0) {
      setOnboardingStep(3);
      return;
    }
    if (onboardingStep === 0 && valCount >= 1) {
      // User added at least 1 value → prompt anti-values
      setOnboardingStep(1);
      setBrowserTab("anti");
    } else if (onboardingStep === 1 && antiCount >= 1) {
      // User added at least 1 anti-value → encouragement
      setOnboardingStep(2);
    }
  }, [valCount, antiCount, onboardingStep, initialLoadDone]);

  /* ─── Place / Remove / Swap ─── */
  const placeValue = useCallback((predefined, slotIndex) => {
    if (pyramidValues.some(v => v && v.name === predefined.name)) {
      toast.error("Already in your pyramid");
      return;
    }
    const val = {
      value_id: `val_${Date.now().toString(36)}`,
      // Preserve the catalog `id` so <ValueIcon> can resolve the SVG medallion
      // on placed cards (and survive a round-trip through the backend).
      id: predefined.id,
      name: predefined.name,
      description: predefined.definition,
      definition: predefined.definition,
      category: predefined.category,
    };
    const next = [...pyramidValues];
    next[slotIndex] = val;
    setPyramidValues(next);
    autoSave(next, pyramidAntiValues);
    trackValuePlaced({ name: val.name, position: slotIndex + 1, type: "value" });
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const placeAntiValue = useCallback((predefined, slotIndex) => {
    if (pyramidAntiValues.some(v => v && v.name === predefined.name)) {
      toast.error("Already in your pyramid");
      return;
    }
    const val = {
      value_id: `aval_${Date.now().toString(36)}`,
      id: predefined.id,
      name: predefined.name,
      description: predefined.definition,
      definition: predefined.definition,
      category: predefined.category,
    };
    const next = [...pyramidAntiValues];
    next[slotIndex] = val;
    setPyramidAntiValues(next);
    autoSave(pyramidValues, next);
    trackValuePlaced({ name: val.name, position: slotIndex + 1, type: "anti_value" });
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const removeValue = useCallback((slotIndex) => {
    const removed = pyramidValues[slotIndex];
    const next = [...pyramidValues];
    next[slotIndex] = null;
    // Compact: shift values up
    const compacted = next.filter(Boolean);
    const final = Array(6).fill(null);
    compacted.forEach((v, i) => { final[i] = v; });
    setPyramidValues(final);
    autoSave(final, pyramidAntiValues);
    if (removed) trackValueRemoved({ name: removed.name, type: "value" });
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const removeAntiValue = useCallback((slotIndex) => {
    const removed = pyramidAntiValues[slotIndex];
    const next = [...pyramidAntiValues];
    next[slotIndex] = null;
    const compacted = next.filter(Boolean);
    const final = Array(6).fill(null);
    compacted.forEach((v, i) => { final[i] = v; });
    setPyramidAntiValues(final);
    autoSave(pyramidValues, final);
    if (removed) trackValueRemoved({ name: removed.name, type: "anti_value" });
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const swapValues = useCallback((from, to) => {
    const next = [...pyramidValues];
    [next[from], next[to]] = [next[to], next[from]];
    setPyramidValues(next);
    autoSave(next, pyramidAntiValues);
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const swapAntiValues = useCallback((from, to) => {
    const next = [...pyramidAntiValues];
    [next[from], next[to]] = [next[to], next[from]];
    setPyramidAntiValues(next);
    autoSave(pyramidValues, next);
  }, [pyramidValues, pyramidAntiValues, autoSave]);

  const quickAddValue = useCallback((predefined) => {
    const emptyIdx = pyramidValues.findIndex(v => v === null);
    if (emptyIdx === -1) { toast.error("Pyramid is full (max 6)"); return; }
    placeValue(predefined, emptyIdx);
  }, [pyramidValues, placeValue]);

  const quickAddAntiValue = useCallback((predefined) => {
    const emptyIdx = pyramidAntiValues.findIndex(v => v === null);
    if (emptyIdx === -1) { toast.error("Pyramid is full (max 6)"); return; }
    placeAntiValue(predefined, emptyIdx);
  }, [pyramidAntiValues, placeAntiValue]);

  /* ─── DnD Handlers ─── */
  const handleDragStart = (event) => setActiveDragData(event.active.data.current);
  const handleDragEnd = (event) => {
    setActiveDragData(null);
    const { active, over } = event;
    if (!over) return;
    const a = active.data.current;
    const o = over.data.current;

    if (a.type === "browser-values" && o.type === "slot-values") placeValue(a.value, o.slotIndex);
    else if (a.type === "browser-anti" && o.type === "slot-anti") placeAntiValue(a.value, o.slotIndex);
    else if (a.type === "pyramid-values" && o.type === "slot-values" && a.slotIndex !== o.slotIndex) swapValues(a.slotIndex, o.slotIndex);
    else if (a.type === "pyramid-anti" && o.type === "slot-anti" && a.slotIndex !== o.slotIndex) swapAntiValues(a.slotIndex, o.slotIndex);
  };

  /* ─── Browser filter ─── */
  const currentPredefined = browserTab === "values" ? predefinedValues : predefinedAntiValues;
  const currentCategories = browserTab === "values" ? categories : antiCategories;
  const currentPlaced = browserTab === "values" ? pyramidValues : pyramidAntiValues;

  const filteredItems = currentPredefined.filter(v => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || v.name.toLowerCase().includes(q) || v.definition.toLowerCase().includes(q);
    const matchCat = selectedCategory === "all" || v.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const isItemPlaced = (item) => currentPlaced.some(p => p && p.name === item.name);

  /* ─── Pyramid row render helper ─── */
  const renderRow = (slots, startIdx, count, type) => (
    <div className="flex justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
      {Array.from({ length: count }, (_, i) => {
        const idx = startIdx + i;
        const hints = type === "values" ? VALUE_SLOT_HINTS : ANTI_SLOT_HINTS;
        return (
          <div key={idx} className="w-[100px] sm:w-[135px] lg:w-[155px]">
            <PyramidSlot
              slotId={`slot-${type}-${idx}`}
              slotIndex={idx}
              value={slots[idx]}
              type={type}
              onRemove={type === "values" ? removeValue : removeAntiValue}
              activeDrag={activeDragData}
              rankLabel={`#${idx + 1}`}
              hint={hints[idx]}
            />
          </div>
        );
      })}
    </div>
  );

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div data-testid="values-tool-page">
        {guest && (
          <SEO
            title="Free Core Values Assessment Tool"
            description="Discover your top 6 core values and anti-values with this free visual pyramid tool. Explore 50 values and 50 anti-values to define what matters most to you."
            path="/values"
          />
        )}
        {/* ─── Header ─── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          {/* Guest top bar */}
          {guest && (
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => {
                  const hasWork = pyramidValues.some(Boolean) || pyramidAntiValues.some(Boolean);
                  if (hasWork) setShowAuthGate(true);
                  else navigate("/");
                }}
                className="flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
                data-testid="back-to-home"
              >
                <ArrowLeft className="w-4 h-4" /> {t("common.home")}
              </button>
              <EvoluraLogo size={32} variant="dark" />
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "system-ui, sans-serif" }}>
                {t("coreValues.title")}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {saveStatus === "saving" && (
                <span className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="save-status-saving">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("common.saving")}
                </span>
              )}
              {saveStatus === "saved" && (
                <span className="flex items-center gap-1.5 text-xs text-emerald-500" data-testid="save-status-saved">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {t("common.saved")}
                </span>
              )}
              {guest && (pyramidValues.some(Boolean) || pyramidAntiValues.some(Boolean)) && (
                <Button
                  onClick={() => setShowAuthGate(true)}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-sm"
                  data-testid="guest-save-signin"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {t("common.signInToSave")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ─── Content ─── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">

              {/* (C) Guest Intro Banner */}
              {!introDismissed && valCount === 0 && antiCount === 0 && (
                <div
                  className="mb-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-violet-50 to-emerald-50 border border-violet-100/60 relative"
                  data-testid="intro-banner"
                >
                  <button
                    onClick={() => setIntroDismissed(true)}
                    className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/60 transition-colors"
                    data-testid="dismiss-intro"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>
                    {t("coreValues.introWhatIs")}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
                    Your <strong className="text-emerald-700">values</strong> define who you aspire to be.
                    Your <strong className="text-red-600">anti-values</strong> are patterns you want to leave behind.
                    Place up to 6 of each to build your personal identity diamond — your most important at the top.
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      {t("coreValues.browseRight")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Plus className="w-3 h-3" />
                      {t("coreValues.clickToAdd")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <GripVertical className="w-3 h-3" />
                      {t("coreValues.dragToReorder")}
                    </span>
                  </div>
                </div>
              )}

              {/* (A) Onboarding Stepper Tooltip */}
              {onboardingStep < 3 && initialLoadDone && (
                <div
                  className={`mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    onboardingStep === 0
                      ? "bg-emerald-50 border-emerald-200"
                      : onboardingStep === 1
                        ? "bg-red-50 border-red-200"
                        : "bg-violet-50 border-violet-200"
                  }`}
                  data-testid="onboarding-stepper"
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    onboardingStep === 0
                      ? "bg-emerald-200 text-emerald-800"
                      : onboardingStep === 1
                        ? "bg-red-200 text-red-800"
                        : "bg-violet-200 text-violet-800"
                  }`}>
                    {onboardingStep + 1}
                  </div>
                  <p className="text-sm text-slate-700 flex-1">
                    {ONBOARDING_STEPS[onboardingStep].message}
                  </p>
                  <button
                    onClick={() => setOnboardingStep(3)}
                    className="text-xs text-slate-400 hover:text-slate-600 font-medium whitespace-nowrap"
                    data-testid="dismiss-onboarding"
                  >
                    Got it
                  </button>
                </div>
              )}

              <div className="flex flex-col lg:flex-row gap-6">
                {/* Left: Pyramid diamond */}
                <div className="flex-1 min-w-0">
                  {/* Values Pyramid (pointing up) */}
                  <div className="rounded-3xl bg-gradient-to-b from-emerald-50/60 to-white border border-emerald-100/60 p-4 sm:p-6">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <ChevronUp className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
                        Your Values
                      </span>
                      <span className="text-xs text-emerald-400">({valCount}/6)</span>
                    </div>
                    {renderRow(pyramidValues, 0, 1, "values")}
                    {renderRow(pyramidValues, 1, 2, "values")}
                    {renderRow(pyramidValues, 3, 3, "values")}
                  </div>

                  {/* Diamond divider */}
                  <div className="flex items-center gap-3 my-4 px-6 sm:px-10">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-emerald-300 to-emerald-300" />
                    <div className="w-2.5 h-2.5 rotate-45 border-2 border-slate-300 bg-white flex-shrink-0" />
                    <div className="flex-1 h-px bg-gradient-to-r from-red-300 via-red-300 to-transparent" />
                  </div>

                  {/* Anti-Values Pyramid (pointing down) */}
                  <div className="rounded-3xl bg-gradient-to-t from-red-50/60 to-white border border-red-100/60 p-4 sm:p-6">
                    {renderRow(pyramidAntiValues, 3, 3, "anti")}
                    {renderRow(pyramidAntiValues, 1, 2, "anti")}
                    {renderRow(pyramidAntiValues, 0, 1, "anti")}
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <ChevronDown className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-bold text-red-600 uppercase tracking-wider">
                        Your Anti-Values
                      </span>
                      <span className="text-xs text-red-400">({antiCount}/6)</span>
                    </div>
                  </div>
                </div>

                {/* Right: Browser sidebar */}
                <div className="w-full lg:w-80 lg:flex-shrink-0">
                  <div className="card p-4 lg:sticky lg:top-4">
                    {/* Browser tab toggle */}
                    <div className="flex rounded-xl bg-slate-100 p-1 mb-3" data-testid="browser-tab-toggle">
                      <button
                        onClick={() => { setBrowserTab("values"); setSelectedCategory("all"); setSearchQuery(""); }}
                        className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${
                          browserTab === "values" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="browser-tab-values"
                      >
                        Values (50)
                      </button>
                      <button
                        onClick={() => { setBrowserTab("anti"); setSelectedCategory("all"); setSearchQuery(""); }}
                        className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors ${
                          browserTab === "anti" ? "bg-white text-red-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="browser-tab-anti"
                      >
                        Anti-Values (50)
                      </button>
                    </div>

                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 text-sm"
                        data-testid="browser-search"
                      />
                    </div>

                    {/* Category pills */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <button
                        onClick={() => setSelectedCategory("all")}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                          selectedCategory === "all" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                        data-testid="category-all"
                      >
                        {t("coreValues.allCategory")}
                      </button>
                      {currentCategories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.name)}
                          className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                            selectedCategory === cat.name ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                          data-testid={`category-${cat.id}`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>

                    {/* Card list */}
                    <ScrollArea className="h-[320px] lg:h-[440px]">
                      <div className="space-y-2 pr-2">
                        {filteredItems.length > 0 ? filteredItems.map(item => (
                          <BrowserCard
                            key={item.id}
                            value={item}
                            type={browserTab}
                            onQuickAdd={browserTab === "values" ? quickAddValue : quickAddAntiValue}
                            isPlaced={isItemPlaced(item)}
                          />
                        )) : (
                          <div className="text-center py-8 text-xs text-slate-400">
                            {searchQuery ? "No matches" : "All placed"}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </div>
        </div>
      </div>

      {/* ─── Drag Overlay ─── */}
      <DragOverlay dropAnimation={null}>
        {activeDragData?.value ? (() => {
          const isValuesSide = activeDragData.type?.includes("value");
          return (
            <div className={`p-3 rounded-2xl border-2 shadow-xl max-w-[200px] flex items-start gap-2 ${
              isValuesSide ? "bg-emerald-50 border-emerald-400" : "bg-red-50 border-red-400"
            }`}>
              <ValueIcon
                id={activeDragData.value.id}
                name={activeDragData.value.name}
                anti={!isValuesSide}
                size={32}
              />
              <div className="min-w-0">
                <span className="font-semibold text-sm text-slate-900 block truncate">{activeDragData.value.name}</span>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{activeDragData.value.definition}</p>
              </div>
            </div>
          );
        })() : null}
      </DragOverlay>
      {/* Guest auth gate modal */}
      {guest && (
        <GuestAuthGate
          open={showAuthGate}
          onOpenChange={setShowAuthGate}
          title="Sign in to save your Values"
          subtitle="Your pyramid is safe — it will be saved automatically after signing in."
        />
      )}
    </DndContext>
  );
};

export default CoreValuesTool;
